import {
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  Paper,
  Container,
  InputAdornment,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { TransactionAgreementType } from '../types/transactionAgreement';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Amplify } from 'aws-amplify';
import { post } from 'aws-amplify/api';
import config from '../../../amplifyconfiguration.json';
import { updatePatient } from '../../../graphql/mutations';
import { getPatient } from '../../../graphql/queries';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useCloudSignInfo } from '@/hooks/useCloudSignInfo';
import { useActiveStep } from '../hooks/useActiveStep';
import { executeQuery } from '@/client/graphql';
import { GetPatientQuery, UpdatePatientMutation } from '@/API';
import { executePost } from '@/client/rest';

Amplify.configure(config);

const schema: yup.ObjectSchema<TransactionAgreementType> = yup.object({
  kaisekiKenriNoKyodaku: yup.boolean().required('解析権利の許諾を選択してください。'),
  eturankenKyodaku: yup.boolean().required('閲覧権許諾を選択してください。'),
  tokumeiKakouJouhouHukuseiNoKyodaku: yup.boolean().required('匿名加工による情報複製の許諾を選択してください。'),
  koukokuUketoriNoKyodaku: yup.boolean().required('広告受取り許諾を選択してください。'),
  minimumAdvertisedPrice: yup
    .number()
    .positive('正の数を入力してください。')
    .max(1000, '1000以下で設定してください。')
    .min(0, '0以上で設定してください。')
    .required('一回の広告表示価格を入力してください。')
    .typeError('数値を入力してください。'),
  designationOfMinimumTransactionPrice: yup
    .number()
    .positive('正の数を入力してください。')
    .max(1000, '1000以下で設定してください。')
    .min(0, '0以上で設定してください。')
    .required('取引最低価格を入力してください。')
    .typeError('数値を入力してください。'),
  kouekiKenkyuuMushouRiyou: yup
    .boolean()
    .required('公益利用・研究目的については上記の設定でなく無償提供を行うを選択してください。'),
  tudoKeiyakuKakunin: yup.boolean().required('都度契約締結について確認してください。')
});

export type TransactionAgreementForm = yup.InferType<typeof schema>;

export function TransactionAgreement() {
  // 初期化を検知するフラグ
  const [loading, setLoading] = React.useState(true);
  const [showAccordion, setShowAccordion] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const { setCloudSignInfo } = useCloudSignInfo();
  const { setActiveStep } = useActiveStep();

  const setData = useCallback(async () => {
    setShowAccordion(!showAccordion);
  }, []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      kaisekiKenriNoKyodaku: true,
      eturankenKyodaku: true,
      tokumeiKakouJouhouHukuseiNoKyodaku: true,
      koukokuUketoriNoKyodaku: true,
      minimumAdvertisedPrice: 10,
      designationOfMinimumTransactionPrice: 0,
      kouekiKenkyuuMushouRiyou: true,
      tudoKeiyakuKakunin: false
    }
  });

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDisagree = async () => {
    try {
      const session = await fetchAuthSession();
      if (session.userSub) {
        await executeQuery<UpdatePatientMutation>(updatePatient, {
          input: {
            id: session.userSub,
            torihikiGoui: false
          }
        });
      }
    } catch (e) {
      // TODO: fetchAuthSessionのエラー
      console.error(e);
    }
    setOpenDialog(false);
    setActiveStep('MedicalInterview');
  };

  useEffect(() => {
    // 初期化されていたら処理を抜ける
    if (!loading) {
      return;
    }
    const onload = async () => {
      await setData();
    };
    onload();
    // 初期化済み状態に遷移
    setLoading(false);
  }, [loading, setData]);

  async function onSubmit(event: TransactionAgreementForm) {
    try {
      const session = await fetchAuthSession();
      if (session.userSub) {
        await executeQuery<UpdatePatientMutation>(updatePatient, {
          input: {
            id: session.userSub,
            kaisekiKenriNoKyodaku: event.kaisekiKenriNoKyodaku,
            eturankenKyodaku: event.eturankenKyodaku,
            tokumeiKakouJouhouHukuseiNoKyodaku: event.tokumeiKakouJouhouHukuseiNoKyodaku,
            koukokuUketoriNoKyodaku: event.koukokuUketoriNoKyodaku,
            minimumAdvertisedPrice: event.minimumAdvertisedPrice,
            designationOfMinimumTransactionPrice: event.designationOfMinimumTransactionPrice,
            kouekiKenkyuuMushouRiyou: event.kouekiKenkyuuMushouRiyou,
            tudoKeiyakuKakunin: event.tudoKeiyakuKakunin
          }
        });
      }
    } catch (e) {
      // TODO
      console.log(e);
    }
    setActiveStep('ConfirmationTransactionAgreement');

    const postTokenResult = (await executePost({
      apiName: 'medirecoWebApi',
      path: '/cloud-sign/transaction/token'
    })) as any;

    const token = postTokenResult.access_token;

    const postDocumentsResult = (await executePost({
      apiName: 'medirecoWebApi',
      path: '/cloud-sign/transaction/documents',
      options: {
        body: {
          token
        }
      }
    })) as any;

    const documentId = postDocumentsResult.id;
    const participantId = postDocumentsResult.participants[1].id;

    setCloudSignInfo({ token, documentId, participantId });

    const session = await fetchAuthSession();

    const res = await executeQuery<GetPatientQuery>(getPatient, { id: session.userSub as string }, 'userPool');

    const patientData = res?.getPatient;

    if (patientData) {
      const name = patientData.familyName + patientData.firstName;
      const email = patientData.email;

      await executePost({
        apiName: 'medirecoWebApi',
        path: '/cloud-sign/participants',
        options: {
          body: {
            token,
            documentId,
            participantId,
            name,
            email
          }
        }
      });

      await executePost({
        apiName: 'medirecoWebApi',
        path: '/cloud-sign/participants',
        options: {
          body: {
            token,
            documentId,
            participantId,
            name,
            email
          }
        }
      });
    }
  }

  const toggleAccordion: any = () => {
    setShowAccordion(!showAccordion);
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          個人での情報管理と取引支援サービス
        </Typography>
        <Typography style={{ marginBottom: '12px' }}>
          当院では米国で同様、情報を個人で管理して、AIなどの先進の医療機器に使用できる事を 支援しています。
          <br />
          このサービスを利用するためにはサービス申し込みが必要です。
          <br />
          患者様ー病院とAIBTRUST社（大阪大学発ベンチャー）の特許技術である情報の暗号化とブロックチェーン技術を用いて、情報のハッキングや改ざん防止のためのシステム利用が必要になるため三社間の合意となります（AIBTRSUT社はあなたの情報の閲覧はできません）
          <br />
          システム利用のメリットとデメリットを確認の上、是非ご利用ください
        </Typography>
        <Paper style={{ maxHeight: '420px', padding: '10px', marginBottom: '20px', overflow: 'auto' }}>
          <Typography>
            個人管理の仕組み
            <br />
            　・当院が許諾した検査情報や診断情報、お薬の情報を暗号化してAIBTRUST社システムに保存します（Internet上にばらばらにされて保管されます）
            <br />
            　・情報を解読するため「鍵」を患者様の携帯電話のアプリに送信します
            <br />
            　・お渡しした「鍵」はマイナンバーカードの認証のみで有効になります
            <br />
            　・第三者との利用契約はあなたに通知され一定期間否定アクションがなければ自動的に締結されます
            <br />
            <br />
            診察（医療）情報を個人管理するメリット
            <br />
            　・利用料は無料です
            <br />
            　　※当院またAIBTRUST社は情報が第三者利用された対価の一部を報酬とします
            <br />
            　・情報を個人管理して、その情報を第三者が利用する場合には利用料が支払われます
            <br />
            　　※報酬型情報提供について当院では来年以降での提供になります
            <br />
            　　※情報を提供しても情報は複製されて第三者に渡されることはありません
            <br />
            　　　複製される場合は個人情報を匿名化します。
            <br />
            　・情報を個人管理する事で、当院の治療で「AI機器やプログラム」を利用できます。
            <br />
            　　※AI機器を利用することで誤診などがへり医療品質が向上します
            <br />
            　　※個人管理いただけない場合、都度の契約が必要で病院業務が煩雑なため
            <br />
            　　　当院ではAI機器の利用は行わず診療いたしています。
            <br />
            <br />
            診察（医療）情報を個人管理するデメリット
            <br />
            　・情報を個人管理するため個人情報保護法の対象外になります。　
            <br />
            　　※当院のカルテで管理される情報は引き続き個人情報保護法のもと適正な年数保管されます。
            <br />
            　・サービス利用にはマイナンバーカードでの本人確認が必要です
            <br />
            　・個人情報はあなたの携帯電話に情報を閲覧するための「鍵」が保管されます
            <br />
            　　そのため、携帯電話を紛失するとすべての情報が失われます。
            <br />
            　　※一度失われたデータを復旧する方法はありません。
            <br />
            　　※機種変更については、可能ですが事前にアプリで手続きが必要です（12月公開予定）
            <br />
            　・あなたとの利用契約のない第三者がデータを見たり技術的に改ざんされることは
            <br />
            　　世界的にまだ一度もない技術を使用していますが、
            <br />
            　　もし、情報漏洩した場合でも当院またはAIBTRSUT社は保証しません
            <br />
            　・個人管理サービスを場合、AIを利用した診療は行われず、提供できる医療が少なく
            <br />
            　　なりまた品質が低下します。
          </Typography>
        </Paper>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={0}>
            <Grid item xs={12} sm={8}>
              <Button onClick={toggleAccordion} disabled={showAccordion} variant={showAccordion ? 'contained' : 'text'}>
                おすすめ設定で登録する
              </Button>

              <Button
                onClick={toggleAccordion}
                disabled={!showAccordion}
                variant={!showAccordion ? 'contained' : 'text'}
              >
                自分で設定する
              </Button>
            </Grid>
            {!showAccordion && (
              <Accordion>
                <AccordionSummary id="panel-header" expandIcon={<ExpandMoreIcon />} aria-controls="panel-content">
                  詳細な設定を変更する
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                      <Typography>① 解析権利の許諾</Typography>
                      <Typography variant="body2" gutterBottom>
                        （実データは提供されません）
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="kaisekiKenriNoKyodaku"
                        control={control}
                        defaultValue={false}
                        render={({ field, formState: { errors } }) => (
                          <ToggleButtonGroup
                            exclusive
                            {...field}
                            onChange={(_, value) => field.onChange(value)}
                            value={field.value}
                            fullWidth
                            color="primary"
                          >
                            <ToggleButton value={true}>許諾する</ToggleButton>
                            <ToggleButton value={false}>許諾しない</ToggleButton>
                          </ToggleButtonGroup>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Typography>② 閲覧権許諾</Typography>
                      <Typography variant="body2" gutterBottom>
                        （指定された安全なシステム上での閲覧を許諾する権利）
                        <br />
                        ※他医療施設への情報提供、セカンドオピニオンや複数の医師や研究者によるチェックによる追加データ作成を許諾する
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="eturankenKyodaku"
                        control={control}
                        defaultValue={false}
                        render={({ field, formState: { errors } }) => (
                          <ToggleButtonGroup
                            exclusive
                            {...field}
                            onChange={(_, value) => field.onChange(value)}
                            value={field.value}
                            fullWidth
                            color="primary"
                          >
                            <ToggleButton value={true}>許諾する</ToggleButton>
                            <ToggleButton value={false}>許諾しない</ToggleButton>
                          </ToggleButtonGroup>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Typography>③ 匿名加工による情報複製の許諾</Typography>
                      <Typography variant="body2" gutterBottom>
                        ※取引価格は①、②、③の順に高額に取引される傾向があります。
                        <br />
                        ※あなたのマイナンバーカードや保険証の情報をもたない方以外では、
                        <br />
                        あなたを特定する名前や住所などの情報が閲覧されることはありません。
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="tokumeiKakouJouhouHukuseiNoKyodaku"
                        control={control}
                        defaultValue={false}
                        render={({ field, formState: { errors } }) => (
                          <ToggleButtonGroup
                            exclusive
                            {...field}
                            onChange={(_, value) => field.onChange(value)}
                            value={field.value}
                            fullWidth
                            color="primary"
                          >
                            <ToggleButton value={true}>許諾する</ToggleButton>
                            <ToggleButton value={false}>許諾しない</ToggleButton>
                          </ToggleButtonGroup>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Typography>④ あなたの健康情報をもとにした 広告（健康商品や薬の情報など）を受け取る</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="koukokuUketoriNoKyodaku"
                        control={control}
                        defaultValue={false}
                        render={({ field, formState: { errors } }) => (
                          <ToggleButtonGroup
                            exclusive
                            {...field}
                            onChange={(_, value) => field.onChange(value)}
                            value={field.value}
                            fullWidth
                            color="primary"
                          >
                            <ToggleButton value={true}>許諾する</ToggleButton>
                            <ToggleButton value={false}>許諾しない</ToggleButton>
                          </ToggleButtonGroup>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} display="flex" alignItems="center">
                      <Typography variant="body2" sx={{ mr: 1, textAlign: 'right', width: '100%' }}>
                        一回
                      </Typography>
                      <TextField
                        size="small"
                        margin="normal"
                        fullWidth
                        id="minimumAdvertisedPrice"
                        type="number"
                        InputLabelProps={{
                          shrink: true
                        }}
                        variant="outlined"
                        error={!!errors.minimumAdvertisedPrice}
                        helperText={errors.minimumAdvertisedPrice?.message}
                        {...register('minimumAdvertisedPrice')}
                      />
                      <Typography variant="body2" sx={{ ml: 1, width: '30%' }}>
                        円以下の広告は表示しない
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Typography>⑤ 取引最低価格の指定</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} display="flex" alignItems="center">
                      <TextField
                        margin="normal"
                        fullWidth
                        id="designationOfMinimumTransactionPrice"
                        type="number"
                        InputLabelProps={{
                          shrink: true
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">円 (超)</InputAdornment>
                        }}
                        variant="outlined"
                        error={!!errors.designationOfMinimumTransactionPrice}
                        helperText={errors.designationOfMinimumTransactionPrice?.message}
                        {...register('designationOfMinimumTransactionPrice')}
                      />
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <Typography>⑥ 公益利用・研究目的については上記の 設定でなく無償提供を行う</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="kouekiKenkyuuMushouRiyou"
                        control={control}
                        defaultValue={false}
                        render={({ field, formState: { errors } }) => (
                          <ToggleButtonGroup
                            exclusive
                            {...field}
                            onChange={(_, value) => field.onChange(value)}
                            value={field.value}
                            fullWidth
                            color="primary"
                          >
                            <ToggleButton value={true}>許諾する</ToggleButton>
                            <ToggleButton value={false}>許諾しない</ToggleButton>
                          </ToggleButtonGroup>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={7}></Grid>
                    <Grid item xs={12} sm={5}>
                      <Controller
                        name="tudoKeiyakuKakunin"
                        control={control}
                        defaultValue={false}
                        render={({ field: { onChange, value }, formState: { errors } }) => (
                          <FormControlLabel
                            control={
                              <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)} color="primary" />
                            }
                            label="都度契約締結について確認する"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}
          </Grid>
          <Box display="flex" justifyContent="space-between" mt={2}>
            <Grid container spacing={0}>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setActiveStep('TransactionDescription')}
                  sx={{ mt: 3, mb: 2, margin: 2 }}
                >
                  戻る
                </Button>
              </Grid>
              <Grid item xs={12} sm={1}></Grid>
              <Grid item xs={12} sm={1}></Grid>
              <Grid item xs={12} sm={6} justifyContent="space-between" display="flex">
                <Button onClick={handleOpenDialog} fullWidth variant="contained" sx={{ mt: 3, mb: 2, margin: 2 }}>
                  同意しない
                </Button>
                <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2, margin: 2 }}>
                  同意する
                </Button>
              </Grid>
            </Grid>
          </Box>
        </form>
        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>確認</DialogTitle>
          <DialogContent>
            <DialogContentText>
              個人管理サービスを利用されない場合、AIを利用した診療は行われず、提供できる医療が少なくなり、品質が低下する場合がありますがよろしいでしょうか？
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} variant="contained">
              キャンセル
            </Button>
            <Button onClick={handleDisagree}>同意しない</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}
